import React, { useEffect, useState } from 'react';
import Sidebar from './sidebar';
import axios from '../../utils/axios';
import { format } from 'date-fns';

const History = () => {
  const [timeline, setTimeline] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 2; // properties per page

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const res = await axios.get('/properties/super-history');

        // Sort all actions by latest first
        const sortedData = res.data.sort((a, b) => new Date(b.action_time) - new Date(a.action_time));

        // Group by property_id
        const grouped = sortedData.reduce((acc, item) => {
          if (!acc[item.property_id]) acc[item.property_id] = [];
          acc[item.property_id].push(item);
          return acc;
        }, {});

        // Convert object to array and sort property groups by latest action_time
        const groupedEntries = Object.entries(grouped);
        const sortedGroupedEntries = groupedEntries.sort(([, actionsA], [, actionsB]) => {
          const latestA = new Date(actionsA[0].action_time);
          const latestB = new Date(actionsB[0].action_time);
          return latestB - latestA; // Descending order
        });

        setTimeline(sortedGroupedEntries);
      } catch (err) {
        console.error('Failed to fetch timeline:', err);
      }
    };

    fetchTimeline();
  }, []);

  const capitalize = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const renderRoleAndAction = (item) => {
    const role = item.user_role || item.role || 'Unknown Role';
    const action = item.action || item.action_type || 'performed an action';
    return `${capitalize(role)}: ${action}`;
  };

  const totalPages = Math.ceil(timeline.length / pageSize);
  const paginatedData = timeline.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="flex h-screen">
      <Sidebar />
      <main className="flex-1 bg-gray-50 p-6 overflow-y-auto">
        <section className="bg-white shadow-md rounded-lg p-6 border border-teal-100">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Supervisor Activity (Grouped by Property)</h2>

          {paginatedData.length === 0 ? (
            <p className="text-gray-500">No supervisor history available.</p>
          ) : (
            paginatedData.map(([propertyId, actions]) => (
              <div key={propertyId} className="mb-8">
                <h3 className="text-lg font-semibold text-teal-700 mb-2">Property ID: {propertyId}</h3>
                <ol className="relative border-l border-teal-400 space-y-6 ml-2">
                  {actions.map(item => (
                    <li key={item.id} className="ml-4">
                      <div className="absolute w-3 h-3 bg-teal-500 rounded-full -left-1.5 border border-white" />
                      <time className="block mb-1 text-sm text-gray-400">
                        {format(new Date(item.action_time), 'PPpp')}
                      </time>
                      <div className="text-md font-semibold text-gray-700">{renderRoleAndAction(item)}</div>
                      {item.comments && (
                        <div className="text-sm text-gray-600 italic mt-1">"{item.comments}"</div>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            ))
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-end items-center mt-6 space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 text-sm border rounded ${
                  currentPage === 1
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-teal-700 border-teal-300 hover:bg-teal-50'
                }`}
              >
                Prev
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 text-sm border rounded ${
                  currentPage === totalPages
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-teal-700 border-teal-300 hover:bg-teal-50'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default History;
